import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { assessmentApi } from "../../services/assessmentApi";
import ThinkingOverlay from "@/components/ThinkingOverlay";
import CedExecutiveBanner from "./CedExecutiveBanner";
import { useAdminDemoMode, logAdminDemoEvent } from "@/hooks/useAdminDemoMode";
import { OPPORTUNITY_SCORE_BEST_CASE } from "@/demo/demoProfiles";
import {
  BarChart3, Shield, AlertTriangle, Zap, Clock, ChevronDown, ChevronRight,
  Target, Layers, FileText, CheckCircle2, XCircle, ArrowRight, Sparkles,
  ChevronLeft, Loader2, TrendingUp, Calendar, Users, Settings, Lock,
  Cpu, Eye, Workflow, ArrowDown, MessageSquare, Printer, Download, Send,
} from "lucide-react";

const PILLAR_LABELS = {
  business_strategy: "Business & AI Strategy",
  process_readiness: "Process & Use-Case",
  technology_data: "Technology & Data",
  organizational_readiness: "Org Readiness",
  governance_security: "Governance & Security",
  measurement_value: "Measurement & Value",
  growth_engine: "Growth Engine",
};

const GROWTH_ENGINE_BAND_COLORS = {
  "High Priority": "text-[#ef4444] bg-[#fef2f2] border-[#fecaca]",
  "Moderate Priority": "text-[#2a9ec0] bg-[#f0f9fc]/60 border-[#00485f]/40",
  "Monitor": "text-yellow-400 bg-[#fefce8]/60 border-yellow-800/40",
  "Relatively Mature": "text-[#10b981] bg-[#ecfdf5]/60 border-emerald-800/40",
};

const BAND_COLORS = {
  "Significant Improvement": "text-[#059669] bg-[#ecfdf5] border-[#a7f3d0]",
  "Good Improvement": "text-[#b45309] bg-[#fffbeb] border-[#fde68a]",
  "Self Sufficient": "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]",
  // Legacy bands for backward compat
  "Not Started": "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]",
  Emerging: "text-[#b45309] bg-[#fffbeb] border-[#fde68a]",
  Defined: "text-[#a16207] bg-[#fefce8] border-[#fde047]",
  Operational: "text-[#059669] bg-[#ecfdf5] border-[#a7f3d0]",
  Optimized: "text-[#047857] bg-[#d1fae5] border-[#6ee7b7]",
};

// Full tile background colors for the readiness score card
const BAND_TILE_BG = {
  "Significant Improvement": "bg-[#ecfdf5] border-[#a7f3d0]",
  "Good Improvement": "bg-[#fffbeb] border-[#fde68a]",
  "Self Sufficient": "bg-[#fef2f2] border-[#fecaca]",
  "Not Started": "bg-[#fef2f2] border-[#fecaca]",
  Emerging: "bg-[#fffbeb] border-[#fde68a]",
  Defined: "bg-[#fefce8] border-[#fde047]",
  Operational: "bg-[#ecfdf5] border-[#a7f3d0]",
  Optimized: "bg-[#d1fae5] border-[#6ee7b7]",
};

// Pills with solid white text on solid color — for dark tile contexts
const BAND_PILL = {
  "Significant Improvement": "bg-[#10b981] text-white border-[#10b981]",
  "Good Improvement": "bg-[#f59e0b] text-white border-[#f59e0b]",
  "Self Sufficient": "bg-[#ef4444] text-white border-[#ef4444]",
  "Not Started": "bg-[#ef4444] text-white border-[#ef4444]",
  Emerging: "bg-[#f59e0b] text-white border-[#f59e0b]",
  Defined: "bg-[#eab308] text-white border-[#eab308]",
  Operational: "bg-[#10b981] text-white border-[#10b981]",
  Optimized: "bg-[#059669] text-white border-[#059669]",
};

const DELIVERY_LABELS = {
  ready_built: "Ready-Built Agent",
  openclaw_orchestration: "OpenClaw Orchestration",
  custom_build: "Custom Build",
};

const DELIVERY_ICONS = {
  ready_built: Cpu,
  openclaw_orchestration: Workflow,
  custom_build: Settings,
};

const DELIVERY_COLORS = {
  ready_built: "bg-[#ecfdf5] text-[#059669] border-[#a7f3d0]",
  openclaw_orchestration: "bg-[#eff6ff] text-[#1a56db] border-[#bfdbfe]",
  custom_build: "bg-[#faf5ff] text-[#7c3aed] border-[#e9d5ff]",
};

const READINESS_COLORS = {
  ready: "bg-[#ecfdf5] text-[#059669] border-[#a7f3d0]",
  needs_prep: "bg-[#fffbeb] text-[#b45309] border-[#fde68a]",
  blocked: "bg-[#fef2f2] text-[#b91c1c] border-[#fecaca]",
};

/* ─── Pillar Score Bars ─────────────────────────────────────────── */
const PillarBars = ({ pillarScores }) => (
  <div className="space-y-4">
    {pillarScores.map((ps) => {
      const isGrowth = ps.pillar === "growth_engine";
      // Growth engine: 1-5 scale (5=weakest), others: 0-4 scale (4=best)
      const maxScore = isGrowth ? 5 : 4;
      const pct = Math.min((ps.score / maxScore) * 100, 100);
      let barColor;
      if (isGrowth) {
        // Inverted: higher = weaker = red
        barColor = ps.score >= 4 ? "bg-[#ef4444]" : ps.score >= 3 ? "bg-[#007a9e]" : ps.score >= 2 ? "bg-yellow-500" : "bg-[#10b981]";
      } else {
        barColor = ps.score >= 3.5 ? "bg-green-500" : ps.score >= 3.0 ? "bg-[#10b981]" : ps.score >= 2.0 ? "bg-yellow-500" : ps.score >= 1.0 ? "bg-[#007a9e]" : "bg-[#ef4444]";
      }
      const bandColors = isGrowth ? (GROWTH_ENGINE_BAND_COLORS[ps.band] || "bg-white text-[#9ca3af]") : (BAND_COLORS[ps.band] || "bg-white text-[#9ca3af]");
      return (
        <div key={ps.pillar} data-testid={`pillar-bar-${ps.pillar}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm text-[#374151]">{PILLAR_LABELS[ps.pillar] || ps.label}</span>
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-[#111827] tabular-nums">{ps.score}</span>
              <span className="text-xs text-[#9ca3af]">/{maxScore}</span>
              <Badge className={`text-xs ${bandColors}`}>{ps.band}</Badge>
            </div>
          </div>
          <div className="h-3 bg-white rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-700 ease-out ${barColor}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      );
    })}
  </div>
);

/* ─── Workflow Card ──────────────────────────────────────────────── */
const WorkflowCard = ({ workflow, index }) => {
  const [expanded, setExpanded] = useState(false);
  const DeliveryIcon = DELIVERY_ICONS[workflow.delivery_method] || Settings;
  return (
    <Card className="border border-[#e5e7eb] bg-white overflow-hidden hover:border-[#e5e7eb] transition-all" data-testid={`workflow-card-${index}`}>
      <CardContent className="p-0">
        <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-5 hover:bg-[#fafafa] transition-colors">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-[#eff6ff] flex items-center justify-center shrink-0">
                <span className="text-base font-bold text-[#1a56db]">{index + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-base font-semibold text-[#111827] leading-tight">{workflow.workflow_name}</h4>
                <p className="text-sm text-[#9ca3af] mt-0.5">{workflow.department}{workflow.business_owner ? ` — ${workflow.business_owner}` : ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className={`text-xs ${READINESS_COLORS[workflow.readiness_status] || ""}`}>
                {workflow.readiness_status === "ready" ? "Ready" : workflow.readiness_status === "needs_prep" ? "Needs Prep" : "Blocked"}
              </Badge>
              <Badge className={`text-xs ${DELIVERY_COLORS[workflow.delivery_method] || "bg-white text-[#9ca3af]"}`}>
                <DeliveryIcon className="h-3.5 w-3.5 mr-1" />{DELIVERY_LABELS[workflow.delivery_method] || workflow.delivery_method}
              </Badge>
              {expanded ? <ChevronDown className="h-4 w-4 text-[#9ca3af]" /> : <ChevronRight className="h-4 w-4 text-[#9ca3af]" />}
            </div>
          </div>
          {workflow.why_ai_fits && <p className="text-sm text-[#6b7280] mt-3 line-clamp-2 pl-11">{workflow.why_ai_fits}</p>}
        </button>

        {expanded && (
          <div className="border-t border-[#e5e7eb] p-5 space-y-5 bg-[#f9fafb]">
            {/* Key Metrics Row */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: "AI Pattern", value: workflow.ai_pattern?.replace(/_/g, " "), icon: Cpu },
                { label: "Complexity", value: workflow.complexity_rating, icon: Layers },
                { label: "ROI Confidence", value: workflow.roi_confidence, icon: TrendingUp },
                { label: "Phase", value: workflow.phase_recommendation, icon: Calendar },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="p-3 rounded-lg bg-[#f9fafb] border border-[#e5e7eb]">
                  <div className="flex items-center gap-1.5 mb-1"><Icon className="h-3.5 w-3.5 text-[#9ca3af]" /><span className="text-sm text-[#9ca3af]">{label}</span></div>
                  <span className="text-sm text-[#111827] capitalize">{value || "—"}</span>
                </div>
              ))}
            </div>

            {workflow.current_state_summary && (
              <div>
                <div className="text-sm text-[#9ca3af] uppercase tracking-wider mb-2">Current State</div>
                <p className="text-sm text-[#4b5563] leading-relaxed">{workflow.current_state_summary}</p>
              </div>
            )}

            {workflow.main_pain_points?.length > 0 && (
              <div>
                <div className="text-sm text-[#9ca3af] uppercase tracking-wider mb-2">Pain Points</div>
                <div className="space-y-2">
                  {workflow.main_pain_points.map((p, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-[#4b5563] leading-relaxed">
                      <AlertTriangle className="h-3.5 w-3.5 text-[#b45309] mt-0.5 shrink-0" />{p}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {workflow.delivery_rationale && (
              <div>
                <div className="text-sm text-[#9ca3af] uppercase tracking-wider mb-2">Delivery Rationale</div>
                <p className="text-sm text-[#4b5563] leading-relaxed">{workflow.delivery_rationale}</p>
              </div>
            )}

            {workflow.human_in_loop && (
              <div>
                <div className="text-sm text-[#9ca3af] uppercase tracking-wider mb-2">Human-in-the-Loop</div>
                <p className="text-sm text-[#4b5563] leading-relaxed">{workflow.human_in_loop}</p>
              </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 gap-3">
              {workflow.baseline_kpi && (
                <div className="p-4 rounded-lg border border-[#e5e7eb] bg-[#fafafa]">
                  <div className="text-sm text-[#9ca3af] uppercase tracking-wider">Baseline KPI</div>
                  <div className="text-sm text-[#111827] mt-1.5 leading-relaxed">{workflow.baseline_kpi}</div>
                </div>
              )}
              {workflow.kpi_target && (
                <div className="p-4 rounded-lg border border-emerald-800/30 bg-[#ecfdf5]/20">
                  <div className="text-sm text-[#10b981]/70 uppercase tracking-wider">Target KPI</div>
                  <div className="text-sm text-emerald-300 mt-1.5 leading-relaxed">{workflow.kpi_target}</div>
                </div>
              )}
            </div>

            {/* Systems & Dependencies */}
            {(workflow.systems_involved?.length > 0 || workflow.required_dependencies?.length > 0) && (
              <div className="flex flex-wrap gap-1.5">
                {workflow.systems_involved?.map((s, i) => <Badge key={`sys-${i}`} className="text-xs bg-[#f9fafb] text-[#6b7280] border-[#e5e7eb]">{s}</Badge>)}
                {workflow.required_dependencies?.map((d, i) => <Badge key={`dep-${i}`} className="text-xs bg-[#fffbeb]/30 text-[#007a9e] dark:text-[#f59e0b]/70 border-amber-800/30">{d}</Badge>)}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ─── Roadmap Timeline ──────────────────────────────────────────── */
const RoadmapTimeline = ({ roadmap }) => {
  if (!roadmap) return null;
  const phases = [
    { key: "days_0_30", label: "Days 0-30", subtitle: "Foundation & Discovery", color: "border-l-blue-500", dot: "bg-blue-500", bg: "bg-blue-950/20" },
    { key: "days_31_60", label: "Days 31-60", subtitle: "Build & Pilot", color: "border-l-amber-500", dot: "bg-[#f59e0b]", bg: "bg-[#fffbeb]/20" },
    { key: "days_61_90", label: "Days 61-90", subtitle: "Optimize & Scale", color: "border-l-emerald-500", dot: "bg-[#10b981]", bg: "bg-[#ecfdf5]/20" },
  ];
  return (
    <div className="space-y-8">
      {phases.map(({ key, label, subtitle, color, dot, bg }) => {
        const items = roadmap[key] || [];
        if (!items.length) return null;
        return (
          <div key={key} className={`border-l-2 ${color} pl-6`} data-testid={`roadmap-${key}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-3.5 h-3.5 rounded-full ${dot} ring-4 ring-black/50`} />
              <div>
                <span className="text-base font-semibold text-[#111827]">{label}</span>
                <span className="text-sm text-[#9ca3af] ml-2">{subtitle}</span>
              </div>
            </div>
            <div className="space-y-3 ml-6">
              {items.map((item, i) => (
                <div key={i} className={`flex items-start gap-3 p-4 rounded-lg ${bg} border border-[#e5e7eb]`}>
                  <CheckCircle2 className="h-4 w-4 text-[#9ca3af] mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm text-[#111827] leading-relaxed">{item.task}</div>
                    {item.deliverable && <div className="text-sm text-[#9ca3af] mt-1 leading-relaxed">{item.deliverable}</div>}
                  </div>
                  {item.owner && <Badge className="text-xs bg-[#f9fafb] text-[#9ca3af] border-[#e5e7eb] shrink-0">{item.owner}</Badge>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ─── SOW Section ───────────────────────────────────────────────── */
const SOWSection = ({ sow }) => {
  if (!sow) return null;
  return (
    <div className="space-y-6" data-testid="sow-section">
      {sow.project_title && <h4 className="text-lg font-semibold text-[#111827]">{sow.project_title}</h4>}

      <div className="grid grid-cols-2 gap-6">
        {sow.business_goals?.length > 0 && (
          <div>
            <div className="text-sm text-[#9ca3af] uppercase tracking-wider mb-3">Business Goals</div>
            <ul className="space-y-2">{sow.business_goals.map((g, i) => <li key={i} className="text-sm text-[#4b5563] leading-relaxed flex items-start gap-2"><Target className="h-4 w-4 text-[#1a56db] mt-0.5 shrink-0" />{g}</li>)}</ul>
          </div>
        )}
        {sow.systems_in_scope?.length > 0 && (
          <div>
            <div className="text-sm text-[#9ca3af] uppercase tracking-wider mb-3">Systems in Scope</div>
            <div className="flex flex-wrap gap-2">{sow.systems_in_scope.map((s, i) => <Badge key={i} className="text-sm bg-[#f9fafb] text-[#6b7280] border-[#e5e7eb]">{s}</Badge>)}</div>
          </div>
        )}
      </div>

      {sow.milestones?.length > 0 && (
        <div>
          <div className="text-sm text-[#9ca3af] uppercase tracking-wider mb-3">Milestones</div>
          <div className="space-y-3">
            {sow.milestones.map((m, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-lg bg-[#fafafa] border border-[#e5e7eb]">
                <Badge className="text-sm bg-[#eff6ff] text-[#1a56db] border-[#bfdbfe] shrink-0">{m.name}</Badge>
                <span className="text-sm text-[#4b5563] flex-1 leading-relaxed">{m.deliverable}</span>
                <span className="text-sm text-[#9ca3af] shrink-0">{m.target_date}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sow.risks?.length > 0 && (
        <div>
          <div className="text-sm text-[#9ca3af] uppercase tracking-wider mb-3">Risks & Mitigations</div>
          <div className="space-y-3">
            {sow.risks.map((r, i) => (
              <div key={i} className="p-4 rounded-lg bg-[#fef2f2] border border-[#fecaca]">
                <div className="text-sm text-[#ef4444]/80 leading-relaxed">{r.risk}</div>
                <div className="text-sm text-[#10b981]/70 mt-1.5 leading-relaxed">Mitigation: {r.mitigation}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {sow.success_criteria?.length > 0 && (
          <div>
            <div className="text-sm text-[#9ca3af] uppercase tracking-wider mb-3">Success Criteria</div>
            <ul className="space-y-1.5">{sow.success_criteria.map((c, i) => <li key={i} className="text-sm text-[#4b5563] flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#059669] mt-0.5 shrink-0" />{c}</li>)}</ul>
          </div>
        )}
        {sow.kpi_targets?.length > 0 && (
          <div>
            <div className="text-sm text-[#9ca3af] uppercase tracking-wider mb-3">KPI Targets</div>
            <div className="space-y-3">
              {sow.kpi_targets.map((k, i) => (
                <div key={i} className="p-4 rounded-lg bg-[#fafafa] border border-[#e5e7eb]">
                  <div className="text-sm text-[#374151] font-medium">{k.kpi}</div>
                  <div className="flex items-center gap-2 mt-1.5 text-sm">
                    <span className="text-[#9ca3af]">{k.baseline}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-[#1a56db]" />
                    <span className="text-[#10b981]">{k.target}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {sow.roles?.length > 0 && (
        <div>
          <div className="text-sm text-[#9ca3af] uppercase tracking-wider mb-3">Roles & Responsibilities</div>
          <div className="grid grid-cols-2 gap-3">
            {sow.roles.map((r, i) => (
              <div key={i} className="p-4 rounded-lg bg-[#fafafa] border border-[#e5e7eb]">
                <div className="text-sm text-[#374151] font-medium">{r.role}</div>
                <div className="text-sm text-[#9ca3af] mt-1 leading-relaxed">{r.responsibility}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Delivery Standard ─────────────────────────────────────────── */
const DeliveryStandardSection = ({ standard }) => {
  if (!standard) return null;
  return (
    <div className="space-y-5" data-testid="delivery-standard">
      {standard.description && <p className="text-base text-[#4b5563] leading-relaxed">{standard.description}</p>}
      <div className="grid grid-cols-2 gap-4">
        {(standard.components || []).map((c, i) => (
          <div key={i} className="p-5 rounded-lg bg-[#fafafa] border border-[#e5e7eb] hover:border-[#e5e7eb] transition-all">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded bg-[#eff6ff] flex items-center justify-center"><Settings className="h-3.5 w-3.5 text-[#1a56db]" /></div>
              <span className="text-sm font-semibold text-[#111827]">{c.name}</span>
            </div>
            <p className="text-sm text-[#6b7280] leading-relaxed">{c.description}</p>
            {c.rationale && <p className="text-sm text-[#9ca3af] mt-2 italic leading-relaxed">{c.rationale}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};


/* ════════════════════════════════════════════════════════════════════
   MAIN ASSESSMENT REPORT
   ════════════════════════════════════════════════════════════════════ */

import CEOReportV2 from "./admin/CEOReportV2";

const AssessmentReport = ({ session, resultId, onBack, portalMode = false, externalActiveSection = null, onActiveSectionChange = null }) => {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [generatingShortlist, setGeneratingShortlist] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [loading, setLoading] = useState(true);
  const [internalActiveSection, setInternalActiveSection] = useState("summary");
  const activeSection = externalActiveSection || internalActiveSection;
  const setActiveSection = (v) => {
    setInternalActiveSection(v);
    if (onActiveSectionChange) onActiveSectionChange(v);
  };
  const [prospectPayment, setProspectPayment] = useState(null);
  const [prospectInfo, setProspectInfo] = useState(null);
  const [pricing, setPricing] = useState({ detailed_assessment: 4999, implementation_project: 9999 });
  const [qaData, setQaData] = useState(null);
  const [qaLoading, setQaLoading] = useState(false);
  const [thinkingType, setThinkingType] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [emailingReport, setEmailingReport] = useState(false);
  const reportRef = useRef(null);

  // ── Admin demo: Cmd/Ctrl+Shift+D auto-fills best-case AI Opportunity Score ──
  const { eligible: demoEligible, on: demoOn } = useAdminDemoMode(session);
  useEffect(() => {
    if (!demoEligible || !demoOn) return undefined;
    const handler = (e) => {
      // Cmd+Shift+0 (Mac) / Ctrl+Shift+0 (Windows). Safe across Chrome, Safari,
      // Firefox on both platforms — no native browser or macOS shortcut conflict.
      const trigger = e.shiftKey && (e.metaKey || e.ctrlKey) && (e.code === "Digit0");
      if (!trigger) return;
      e.preventDefault();
      const industry = result?.industry;
      if (!industry) {
        toast.warning("Select an industry first");
        return;
      }
      const bestCase = OPPORTUNITY_SCORE_BEST_CASE[industry];
      if (!bestCase) {
        toast.error(`No best-case profile for industry "${industry}"`);
        return;
      }
      // Confirmation toast — apply if user clicks Confirm
      toast(`Auto-fill ready — apply best-case data for ${bestCase.industry_label}?`, {
        action: {
          label: "Confirm",
          onClick: () => {
            logAdminDemoEvent({
              feature: "opportunity_score_autofill",
              session,
              extra: { industry, result_id: result?.id },
            });
            const merged = {
              ...result,
              overall_readiness_score: bestCase.overall_score / 20, // 1-5 scale
              ai_maturity_score: bestCase.ai_maturity_score,
              workflow_friction_score: bestCase.workflow_friction_score,
              demo_best_case: bestCase,
              workflow_shortlist: (bestCase.top_workflows || []).map((w) => ({
                workflow_name: w.name,
                outcome_statement: w.description,
                relevance_to_answers: `Estimated annual ROI: ${w.estimated_annual_roi}`,
                business_owner: "Executive Sponsor",
                main_pain_points: [],
                why_ai_fits: w.description,
                required_dependencies: [],
                human_in_loop: "Final approval on customer-facing output",
                baseline_kpi: "Today's manual baseline",
                kpi_target: w.estimated_annual_roi,
                roi_confidence: w.impact === "High" ? "high" : "medium",
                rank: w.rank,
                impact: w.impact,
              })),
            };
            setResult(merged);
            toast.success(`Best-case data applied for ${bestCase.industry_label}`);
          },
        },
        duration: 8000,
      });
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [demoEligible, demoOn, result, session]);

  const REPORT_THINKING = {
    implementationPlan: [
      "Analyzing workflow opportunities...",
      "Building your implementation roadmap...",
      "Configuring delivery timelines...",
      "Generating your Implementation Plan...",
      "Finalizing recommendations...",
    ],
  };

  // Fetch dynamic pricing
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${BACKEND_URL}/api/pricing/public`)
      .then(r => r.json())
      .then(d => {
        const items = d.pricing_items || [];
        const detailed = items.find(i => i.key === "detailed_assessment" || i.name?.toLowerCase().includes("detailed"));
        const impl = items.find(i => i.key === "implementation_project" || i.name?.toLowerCase().includes("implementation"));
        setPricing({
          detailed_assessment: detailed?.price || 4999,
          implementation_project: impl?.price || 9999,
        });
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      setResult(resultId);
      // Fetch prospect payment status if we have prospect_id
      if (resultId?.prospect_id) {
        try {
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
          const tenantId = session?.tenant?.id || "tenant_1";
          const userId = session?.user?.id || "public";
          const role = session?.user?.role || "client";
          const resp = await fetch(`${BACKEND_URL}/api/prospects/${resultId.prospect_id}`, {
            headers: { "x-tenant-id": tenantId, "x-user-id": userId, "x-user-role": role },
          });
          if (resp.ok) {
            const prospData = await resp.json();
            setProspectPayment(prospData.payment || {});
            setProspectInfo({
              company_name: prospData.account?.name || prospData.company_name || prospData.company,
              contact_name: prospData.contact?.full_name,
              contact_email: prospData.contact?.email,
              created_at: prospData.created_at,
            });
          }
        } catch { /* ignore */ }
      }
    } catch {
      toast.error("Failed to load results");
    } finally {
      setLoading(false);
    }
  }, [resultId, session]);

  useEffect(() => { load(); }, [load]);

  // Load Q&A when section is active
  useEffect(() => {
    if (activeSection === "qa" && result?.id && !qaData && !qaLoading) {
      setQaLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const tenantId = session?.tenant?.id || "tenant_1";
      const userId = session?.user?.id || "public";
      const role = session?.user?.role || "client";
      fetch(`${BACKEND_URL}/api/assessments/results/${result.id}/qa`, {
        headers: { "x-tenant-id": tenantId, "x-user-id": userId, "x-user-role": role },
      })
        .then(r => r.json())
        .then(d => { setQaData(d.qa || []); setQaLoading(false); })
        .catch(() => { setQaLoading(false); toast.error("Failed to load Q&A"); });
    }
  }, [activeSection, result, qaData, qaLoading, session]);

  const handleGenerateShortlist = async () => {
    setGeneratingShortlist(true);
    try {
      const updated = await assessmentApi.generateShortlist(result.id, session);
      setResult(updated);
      toast.success("Workflow shortlist generated!");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 402) toast.error("LLM budget exceeded", { description: "Add balance in Profile > Universal Key." });
      else toast.error(detail || "Failed to generate shortlist");
    } finally {
      setGeneratingShortlist(false);
    }
  };

  const handleGeneratePlan = async () => {
    setGeneratingPlan(true);
    try {
      const updated = await assessmentApi.generatePlan(result.id, session);
      setThinkingType("implementationPlan");
      setTimeout(() => {
        setThinkingType(null);
        setResult(updated);
        setActiveSection("workflows");
        toast.success("Implementation plan generated!");
        setGeneratingPlan(false);
      }, 5000);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 402) toast.error("LLM budget exceeded", { description: "Add balance in Profile > Universal Key." });
      else toast.error(detail || "Failed to generate plan");
      setGeneratingPlan(false);
    }
  };

  if (loading || !result) return <div className="text-center text-[#6b7280] py-12">Loading results...</div>;

  const isInitial = result.assessment_type === "initial";
  const isProjectPaid = portalMode || !!prospectPayment?.roadmap_paid;
  const reportTitle = isInitial ? "Initial AI Readiness Report" : "Detailed AI Readiness Report";

  const plan = result.implementation_plan || result.generated_report_json;
  const hasPlan = plan && Object.keys(plan).length > 0;
  const hasShortlist = result.workflow_shortlist?.length > 0;
  const pillarScores = result.readiness_scores_by_pillar || [];
  const gatingFlags = result.gating_flags || [];
  const oppScores = result.opportunity_scores || [];

  // Portal mode: all sections unlocked. Admin mode: gated by payment
  const sections = [
    { key: "summary", label: "Executive Summary", icon: BarChart3 },
    { key: "readiness", label: "Readiness", icon: Shield },
    { key: "growth_engine", label: "Growth Engine", icon: TrendingUp },
    { key: "opportunities", label: "Opportunities", icon: Zap },
    ...(!isInitial ? [{ key: "qa", label: "Assessment Q&A", icon: MessageSquare }] : []),
    ...(!isInitial && (hasShortlist || hasPlan) ? [{ key: "workflows", label: "Workflow Plan", icon: Layers }] : []),
    ...(!isInitial && hasPlan ? [
      { key: "roadmap", label: "30/60/90 Roadmap", icon: Calendar },
      { key: "standard", label: "AI Standard", icon: Settings },
    ] : []),
  ];

  // Font size classes for portal mode (larger, more readable — matches Executive Summary)
  const textBase = portalMode ? "text-base leading-relaxed" : "text-sm";
  const textSm = portalMode ? "text-sm leading-relaxed" : "text-xs";
  const textXs = portalMode ? "text-sm leading-relaxed" : "text-[11px]";
  const headingLg = "text-xl sm:text-2xl";
  const headingSm = "text-base";

  // ─── Section content (shared between portal sidebar and admin tabs) ─────
  const ReportSectionContent = () => (
    <div className="space-y-6">
      {/* EXECUTIVE SUMMARY */}
      {activeSection === "summary" && (
        <div className="space-y-6" data-testid="summary-section">
          {/* CED-specific executive banner (renders only for Community & Economic Development industry) */}
          {result.industry === "community_economic_development" && (
            <CedExecutiveBanner result={result} textBase={textBase} textSm={textSm} />
          )}
          <div className="grid grid-cols-4 gap-4">
            <Card className={`border ${BAND_TILE_BG[result.readiness_band] || "border-[#e5e7eb] bg-white"}`}>
              <CardContent className="p-4">
                <div className={`${textSm} text-[#9ca3af] uppercase tracking-wider mb-2`}>Readiness Score</div>
                <div className="text-3xl font-bold text-[#111827] tabular-nums">{result.overall_readiness_score}</div>
                <Badge className={`mt-2 ${textSm} ${BAND_PILL[result.readiness_band] || ""}`}>{result.readiness_band}</Badge>
              </CardContent>
            </Card>
            {(() => {
              const geAnalysis = result.growth_engine_analysis || {};
              const gePillar = pillarScores.find(p => p.pillar === "growth_engine");
              const geScore = gePillar?.score || geAnalysis.category_score || 0;
              const geBand = gePillar?.band || geAnalysis.band || "";
              const geTileBg = geScore >= 4 ? "bg-[#fef2f2] border-[#fecaca]" : geScore >= 3 ? "bg-[#f0f9fc] border-[#bfe3ee]" : geScore >= 2 ? "bg-[#fefce8] border-[#fde047]" : "bg-[#ecfdf5] border-[#a7f3d0]";
              const geBandColor = GROWTH_ENGINE_BAND_COLORS[geBand] || "";
              return (
                <Card className={`border ${geTileBg}`}>
                  <CardContent className="p-4">
                    <div className={`${textSm} text-[#9ca3af] uppercase tracking-wider mb-2`}>Growth Engine</div>
                    <div className="text-3xl font-bold text-[#111827] tabular-nums">{geScore}<span className="text-sm text-[#9ca3af]">/5</span></div>
                    <Badge className={`mt-2 ${textSm} ${geBandColor}`}>{geBand || "N/A"}</Badge>
                  </CardContent>
                </Card>
              );
            })()}
            <Card className="border border-[#e5e7eb] bg-white">
              <CardContent className="p-4">
                <div className={`${textSm} text-[#9ca3af] uppercase tracking-wider mb-2`}>Gating Flags</div>
                <div className="text-2xl font-bold text-[#111827] tabular-nums">{gatingFlags.length}</div>
                {gatingFlags.length > 0 ? <Badge className={`mt-2 ${textSm} bg-[#fef2f2] text-[#ef4444] border-[#fecaca]`}>Action Required</Badge>
                  : <Badge className={`mt-2 ${textSm} bg-[#ecfdf5]/50 text-[#10b981] border-emerald-800/30`}>Clear</Badge>}
              </CardContent>
            </Card>
            <Card className="border border-[#e5e7eb] bg-white">
              <CardContent className="p-4">
                <div className={`${textSm} text-[#9ca3af] uppercase tracking-wider mb-2`}>Workflows</div>
                <div className="text-2xl font-bold text-[#111827] tabular-nums">{hasPlan ? (plan.workflow_plans?.length || 0) : (hasShortlist ? result.workflow_shortlist.length : "—")}</div>
                {(hasShortlist || hasPlan) && <Badge className={`mt-2 ${textSm} bg-blue-950/50 text-[#3b82f6] border-blue-800/30`}>Shortlisted</Badge>}
              </CardContent>
            </Card>
          </div>

          {/* Readiness Score Guided Recommendation */}
          {result.overall_readiness_score != null && (
            <Card className="border border-[#e5e7eb] bg-white" data-testid="readiness-recommendation">
              <CardContent className="p-6">
                <div className={`${textSm} text-[#9ca3af] uppercase tracking-wider mb-3`}>Guided Recommendation</div>
                {result.overall_readiness_score > 4.0 ? (
                  <div className="space-y-2">
                    <p className={`${textBase} text-[#374151] leading-relaxed`}>
                      <strong className="text-[#ef4444]">Self Sufficient.</strong> Your score of <strong>{result.overall_readiness_score}</strong> indicates your organization already has strong AI foundations in place. 589 Labs may not be able to add significant value through a full implementation engagement.
                    </p>
                    <p className={`${textBase} text-[#374151] leading-relaxed`}>
                      We recommend considering a <strong>Retainer Package</strong> for ongoing executive AI consulting, strategic advisory, and staying ahead of emerging AI capabilities.
                    </p>
                  </div>
                ) : result.overall_readiness_score > 2.0 ? (
                  <div className="space-y-2">
                    <p className={`${textBase} text-[#374151] leading-relaxed`}>
                      <strong className="text-[#2a9ec0]">Good Improvement Opportunity.</strong> Your score of <strong>{result.overall_readiness_score}</strong> shows good foundations with clear areas for improvement. This is a strong opportunity for an AI implementation engagement.
                    </p>
                    <p className={`${textBase} text-[#374151] leading-relaxed`}>
                      We recommend proceeding with the <strong>In-Depth Assessment</strong> to identify specific high-impact workflows, followed by a structured <strong>Implementation Project</strong> to accelerate your AI transformation.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className={`${textBase} text-[#374151] leading-relaxed`}>
                      <strong className="text-[#10b981]">Significant Improvement Opportunity.</strong> Your score of <strong>{result.overall_readiness_score}</strong> indicates the greatest opportunity for AI-driven transformation. Organizations at this stage benefit most from a guided, step-by-step approach.
                    </p>
                    <p className={`${textBase} text-[#374151] leading-relaxed`}>
                      We strongly recommend the <strong>In-Depth Assessment</strong> ({`$${pricing.detailed_assessment.toLocaleString()}`}) to map out your AI opportunity landscape, followed by a focused <strong>Implementation Project</strong> ({`$${pricing.implementation_project.toLocaleString()}`}) targeting your highest-ROI workflows.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {hasPlan && plan.executive_summary?.summary_text && (
            <Card className="border border-[#e5e7eb] bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className={`${textSm} text-[#9ca3af] uppercase tracking-wider`}>Executive Summary</div>
                  {plan.executive_summary.recommendation && (
                    <Badge className={`${textSm} bg-[#eff6ff] text-[#1a56db] border-[#bfdbfe]`}>
                      {plan.executive_summary.recommendation}
                    </Badge>
                  )}
                </div>
                <p className={`${textBase} text-[#374151] leading-relaxed whitespace-pre-wrap`}>{plan.executive_summary.summary_text}</p>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Card className="border border-[#fecaca] bg-[#fef2f2]">
              <CardContent className="p-4">
                <div className={`${textSm} text-[#ef4444]/70 uppercase tracking-wider mb-3 flex items-center gap-1.5`}><AlertTriangle className="h-3.5 w-3.5" /> Top Blockers</div>
                <ul className="space-y-2">
                  {(hasPlan ? plan.executive_summary?.top_blockers : result.top_blockers || [])?.map((b, i) => (
                    <li key={i} className={`${textBase} text-[#4b5563] flex items-start gap-2`}><XCircle className="h-4 w-4 text-[#b91c1c] mt-0.5 shrink-0" />{b}</li>
                  ))}
                  {(!result.top_blockers?.length && !plan?.executive_summary?.top_blockers?.length) && <li className={`${textSm} text-[#9ca3af]`}>None identified</li>}
                </ul>
              </CardContent>
            </Card>
            <Card className="border border-emerald-800/20 bg-[#ecfdf5]/10">
              <CardContent className="p-4">
                <div className={`${textSm} text-[#10b981]/70 uppercase tracking-wider mb-3 flex items-center gap-1.5`}><Zap className="h-3.5 w-3.5" /> Top Opportunities</div>
                <ul className="space-y-2">
                  {(hasPlan ? plan.executive_summary?.top_opportunities : result.top_opportunities || [])?.map((o, i) => (
                    <li key={i} className={`${textBase} text-[#4b5563] flex items-start gap-2`}><CheckCircle2 className="h-4 w-4 text-[#059669] mt-0.5 shrink-0" />{o}</li>
                  ))}
                  {(!result.top_opportunities?.length && !plan?.executive_summary?.top_opportunities?.length) && <li className={`${textSm} text-[#9ca3af]`}>None identified</li>}
                </ul>
              </CardContent>
            </Card>
          </div>

          {isInitial ? (
            <Card className="border border-[#bfdbfe] bg-[#eff6ff]">
              <CardContent className="p-6 text-center">
                <Lock className="h-8 w-8 text-[#1a56db] mx-auto mb-3" />
                <h3 className={`${headingSm} font-semibold text-[#111827] mb-1`}>Want the Full Picture?</h3>
                <p className={`${textSm} text-[#6b7280] mb-4 max-w-md mx-auto`}>
                  Upgrade to the In-Depth Assessment ({`$${pricing.detailed_assessment.toLocaleString()}`}) to get a complete workflow analysis.
                </p>
              </CardContent>
            </Card>
          ) : !hasShortlist && !hasPlan ? (
            <Card className="border border-[#bfdbfe] bg-[#eff6ff]">
              <CardContent className="p-6 text-center">
                <Sparkles className="h-8 w-8 text-[#1a56db] mx-auto mb-3" />
                <h3 className={`${headingSm} font-semibold text-[#111827] mb-1`}>Ready for Next Step</h3>
                <p className={`${textSm} text-[#6b7280] mb-4 max-w-md mx-auto`}>
                  Generate a Workflow Shortlist to identify the top 3-5 AI workflows for this organization.
                </p>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      {/* READINESS ANALYSIS */}
      {activeSection === "readiness" && (
        <div className="space-y-6" data-testid="readiness-section">
          <Card className="border border-[#e5e7eb] bg-white">
            <CardContent className="p-6">
              <div className={`${textSm} text-[#9ca3af] uppercase tracking-wider mb-4`}>Readiness by Pillar</div>
              <PillarBars pillarScores={pillarScores} />
            </CardContent>
          </Card>
          {gatingFlags.length > 0 && (
            <Card className="border border-[#fecaca] bg-[#fef2f2]">
              <CardContent className="p-6">
                <div className={`${textSm} text-[#ef4444]/70 uppercase tracking-wider mb-4 flex items-center gap-1.5`}>
                  <Shield className="h-3.5 w-3.5" /> Readiness Gating Flags
                </div>
                <div className="space-y-3">
                  {gatingFlags.map((gf, i) => (
                    <div key={i} className="p-4 rounded-lg bg-[#f9fafb] border border-[#fecaca]" data-testid={`gating-flag-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Lock className="h-3.5 w-3.5 text-[#ef4444]" />
                        <Badge className={`${textSm} bg-[#fef2f2] text-[#ef4444] border-[#fecaca]`}>{gf.rule}</Badge>
                        <span className={`${textXs} text-[#9ca3af]`}>Score: {gf.score}/4.0</span>
                      </div>
                      <p className={`${textBase} text-[#4b5563] leading-relaxed`}>{gf.explanation}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* GROWTH ENGINE ANALYSIS */}
      {activeSection === "growth_engine" && (
        <div className="space-y-6" data-testid="growth-engine-section">
          {(() => {
            const geAnalysis = result.growth_engine_analysis || {};
            const gePillar = pillarScores.find(p => p.pillar === "growth_engine");
            const geScore = gePillar?.score || geAnalysis.category_score || 0;
            const geBand = gePillar?.band || geAnalysis.band || "";
            const geFlags = geAnalysis.flags || [];
            const geRecs = geAnalysis.recommendations || [];
            const geTags = geAnalysis.tags || [];
            const geBandColor = GROWTH_ENGINE_BAND_COLORS[geBand] || "bg-white text-[#9ca3af]";
            const geTileBg = geScore >= 4 ? "bg-[#fef2f2] border-[#fecaca]" : geScore >= 3 ? "bg-[#f0f9fc] border-[#bfe3ee]" : geScore >= 2 ? "bg-[#fefce8] border-[#fde047]" : "bg-[#ecfdf5] border-[#a7f3d0]";

            return (
              <>
                {/* Score Card */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className={`border ${geTileBg}`}>
                    <CardContent className="p-4">
                      <div className={`${textSm} text-[#9ca3af] uppercase tracking-wider mb-2`}>Growth Engine Score</div>
                      <div className="text-3xl font-bold text-[#111827] tabular-nums">{geScore}</div>
                      <div className="text-xs text-[#9ca3af] mb-2">/5.0</div>
                      <Badge className={`${textSm} ${geBandColor}`}>{geBand}</Badge>
                    </CardContent>
                  </Card>
                  <Card className="border border-[#e5e7eb] bg-white">
                    <CardContent className="p-4">
                      <div className={`${textSm} text-[#9ca3af] uppercase tracking-wider mb-2`}>Flags Triggered</div>
                      <div className="text-2xl font-bold text-[#111827] tabular-nums">{geFlags.length}</div>
                      {geFlags.length > 0
                        ? <Badge className={`mt-2 ${textSm} bg-[#fef2f2] text-[#ef4444] border-[#fecaca]`}>Action Required</Badge>
                        : <Badge className={`mt-2 ${textSm} bg-[#ecfdf5]/50 text-[#10b981] border-emerald-800/30`}>No Flags</Badge>
                      }
                    </CardContent>
                  </Card>
                  <Card className="border border-[#e5e7eb] bg-white">
                    <CardContent className="p-4">
                      <div className={`${textSm} text-[#9ca3af] uppercase tracking-wider mb-2`}>Recommendations</div>
                      <div className="text-2xl font-bold text-[#111827] tabular-nums">{geRecs.length}</div>
                      {geTags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {geTags.map((t, i) => <Badge key={i} className="text-xs bg-[#f0f9fc]/30 text-[#2a9ec0] border-[#00485f]/30">{t}</Badge>)}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Scoring Guide */}
                <Card className="border border-[#e5e7eb] bg-white">
                  <CardContent className="p-5">
                    <div className={`${textSm} text-[#9ca3af] uppercase tracking-wider mb-3`}>Score Interpretation</div>
                    <p className={`${textSm} text-[#6b7280] mb-3`}>Growth Engine scoring is inverted: higher scores indicate greater weakness and opportunity for 589 Labs.</p>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { range: "4.0 – 5.0", label: "High Priority", color: "bg-[#fef2f2]/30 border-[#fecaca] text-[#ef4444]" },
                        { range: "3.0 – 3.9", label: "Moderate Priority", color: "bg-[#f0f9fc]/30 border-[#00485f]/30 text-[#2a9ec0]" },
                        { range: "2.0 – 2.9", label: "Monitor", color: "bg-[#fefce8]/30 border-yellow-800/30 text-yellow-400" },
                        { range: "1.0 – 1.9", label: "Relatively Mature", color: "bg-[#ecfdf5]/30 border-emerald-800/30 text-[#10b981]" },
                      ].map(b => (
                        <div key={b.label} className={`p-2.5 rounded-lg border ${b.color} text-center`}>
                          <div className="text-sm font-bold">{b.range}</div>
                          <div className="text-xs mt-0.5">{b.label}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Flags */}
                {geFlags.length > 0 && (
                  <Card className="border border-[#fecaca] bg-[#fef2f2]">
                    <CardContent className="p-6">
                      <div className={`${textSm} text-[#ef4444]/70 uppercase tracking-wider mb-4 flex items-center gap-1.5`}>
                        <AlertTriangle className="h-3.5 w-3.5" /> Growth Engine Flags
                      </div>
                      <div className="space-y-3">
                        {geFlags.map((flag, i) => (
                          <div key={i} className="p-4 rounded-lg bg-[#f9fafb] border border-[#fecaca]" data-testid={`ge-flag-${i}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={`${textSm} ${flag.priority === "High" ? "bg-[#fef2f2] text-[#ef4444] border-[#fecaca]" : "bg-[#fffbeb]/50 text-[#f59e0b] border-amber-800/30"}`}>
                                {flag.priority} Priority
                              </Badge>
                            </div>
                            <p className={`${textSm} text-[#6b7280] mb-2`}>{flag.rule}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {flag.recommendations?.map((r, j) => (
                                <Badge key={j} className="text-xs bg-[#eff6ff] text-[#1a56db] border-[#bfdbfe]">{r}</Badge>
                              ))}
                            </div>
                            {flag.package_types?.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                <span className="text-xs text-[#9ca3af] mr-1">Packages:</span>
                                {flag.package_types.map((p, j) => (
                                  <Badge key={j} className="text-xs bg-[#ecfdf5]/30 text-[#10b981] border-emerald-800/30">{p}</Badge>
                                ))}
                              </div>
                            )}
                            {flag.note && <p className={`${textXs} text-[#9ca3af] mt-2 italic`}>{flag.note}</p>}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recommendations */}
                {geRecs.length > 0 && (
                  <Card className="border border-[#bfdbfe] bg-[#1a56db]/[0.03]">
                    <CardContent className="p-6">
                      <div className={`${textSm} text-[#1a56db]/70 uppercase tracking-wider mb-4 flex items-center gap-1.5`}>
                        <Sparkles className="h-3.5 w-3.5" /> Growth Engine Recommendations
                      </div>
                      <ul className="space-y-2">
                        {geRecs.map((rec, i) => (
                          <li key={i} className={`${textBase} text-[#4b5563] flex items-start gap-2`}>
                            <ArrowRight className="h-4 w-4 text-[#1a56db] mt-0.5 shrink-0" />{rec}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* OPPORTUNITY HEATMAP */}
      {activeSection === "opportunities" && (
        <div className="space-y-4" data-testid="opportunities-section">
          {hasPlan && plan.opportunity_heatmap?.length > 0 ? (
            plan.opportunity_heatmap.map((opp, i) => (
              <div key={i} className={`p-5 rounded-lg border ${
                opp.readiness_label === "ready_now" ? "border-emerald-800/30 bg-[#ecfdf5]/20" :
                opp.readiness_label === "prepare" ? "border-amber-800/30 bg-[#fffbeb]/20" :
                "border-[#fecaca] bg-[#fef2f2]/20"
              }`} data-testid={`heatmap-item-${i}`}>
                <div className="flex items-center justify-between">
                  <h4 className={`${textBase} font-medium text-foreground`}>{opp.workflow}</h4>
                  <Badge className={`${textSm} ${
                    opp.readiness_label === "ready_now" ? "bg-[#ecfdf5]/50 text-[#10b981] border-emerald-800/30" :
                    opp.readiness_label === "prepare" ? "bg-[#fffbeb]/50 text-[#007a9e] dark:text-[#f59e0b] border-amber-800/30" :
                    "bg-[#fef2f2] text-[#ef4444] border-[#fecaca]"
                  }`}>{opp.readiness_label?.replace("_", " ")}</Badge>
                </div>
                <p className={`${textSm} text-[#9ca3af] mt-0.5`}>{opp.department}</p>
                <p className={`${textBase} text-[#4b5563] mt-2 leading-relaxed`}>{opp.explanation}</p>
              </div>
            ))
          ) : (
            oppScores.map((opp, i) => (
              <div key={i} className={`p-5 rounded-lg border ${
                opp.label === "ready_now" ? "border-emerald-800/30 bg-[#ecfdf5]/20" :
                opp.label === "prepare" ? "border-amber-800/30 bg-[#fffbeb]/20" :
                "border-[#fecaca] bg-[#fef2f2]/20"
              }`}>
                <div className="flex items-center justify-between">
                  <span className={`${textBase} text-[#111827] capitalize`}>{opp.category?.replace(/_/g, " ")}</span>
                  <div className="flex items-center gap-2">
                    <span className={`${textSm} text-[#6b7280] tabular-nums`}>Score: {opp.score}/4.0</span>
                    <Badge className={`${textSm} ${
                      opp.label === "ready_now" ? "bg-[#ecfdf5]/50 text-[#10b981]" :
                      opp.label === "prepare" ? "bg-[#fffbeb]/50 text-[#007a9e] dark:text-[#f59e0b]" :
                      "bg-[#fef2f2] text-[#ef4444]"
                    }`}>{opp.label?.replace("_", " ")}</Badge>
                  </div>
                </div>
              </div>
            ))
          )}
          {oppScores.length === 0 && (!plan?.opportunity_heatmap?.length) && (
            <div className={`text-center py-12 text-[#9ca3af] ${textBase}`}>No opportunity data available.</div>
          )}
        </div>
      )}

      {/* ASSESSMENT Q&A */}
      {activeSection === "qa" && (
        <div className="space-y-4" data-testid="qa-section">
          <h3 className={`${headingSm} font-semibold text-foreground`}>In-Depth Assessment Questions & Answers</h3>
          <p className={`${textSm} text-[#9ca3af]`}>All questions and responses from your Detailed AI Readiness Assessment.</p>
          {qaLoading && <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#9ca3af]" /></div>}
          {qaData && qaData.length === 0 && <div className={`text-center py-8 text-[#9ca3af] ${textBase}`}>No Q&A data found for this assessment.</div>}
          {qaData && qaData.length > 0 && (() => {
            const pillars = [...new Set(qaData.map(q => q.pillar))];
            return pillars.map(pillar => (
              <div key={pillar} className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-6 w-6 rounded-md bg-[#eff6ff] flex items-center justify-center">
                    <Shield className="h-3.5 w-3.5 text-[#1a56db]" />
                  </div>
                  <span className="text-sm font-semibold text-[#111827]">{PILLAR_LABELS[pillar] || pillar}</span>
                </div>
                <div className="space-y-2">
                  {qaData.filter(q => q.pillar === pillar).map((qa, qi) => (
                    <Card key={qi} className="border border-[#e5e7eb] bg-[#fafafa]">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className={`${textSm} font-medium text-foreground`}>{qa.question_text}</p>
                            <div className="mt-2 flex items-start gap-2">
                              <span className="text-xs font-medium text-[#1a56db] bg-[#eff6ff] px-2 py-0.5 rounded shrink-0">Answer</span>
                              <p className={`${textSm} text-[#374151]`}>
                                {Array.isArray(qa.answer) ? qa.answer.join(", ") : qa.answer || "—"}
                              </p>
                            </div>
                          </div>
                          <div className={`shrink-0 h-8 w-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                            qa.score >= 3 ? "bg-[#ecfdf5] text-[#059669]" :
                            qa.score >= 2 ? "bg-[#fffbeb] text-[#b45309]" :
                            "bg-[#fef2f2] text-[#b91c1c]"
                          }`}>
                            {qa.score}/4
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* WORKFLOW RECOMMENDATIONS */}
      {activeSection === "workflows" && (
        <div className="space-y-4" data-testid="workflows-section">
          {hasShortlist && !hasPlan && (
            <>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className={`${headingSm} font-semibold text-foreground`}>Workflow Shortlist</h3>
                  <p className={`${textSm} text-[#9ca3af] mt-0.5`}>Top {result.workflow_shortlist.length} workflows ranked by opportunity score.</p>
                </div>
                <Button onClick={handleGeneratePlan} disabled={generatingPlan}
                  className="h-9 bg-[#1a56db] text-white hover:bg-[#1a56db]/90 text-xs font-medium" data-testid="generate-plan-from-workflows">
                  {generatingPlan ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Building Plan...</> : <><FileText className="h-3.5 w-3.5 mr-1.5" /> Generate Full Plan</>}
                </Button>
              </div>
              {result.workflow_shortlist.map((wf, i) => <WorkflowCard key={i} workflow={wf} index={i} />)}
            </>
          )}
          {hasPlan && (plan.workflow_plans || []).map((wf, i) => <WorkflowCard key={i} workflow={wf} index={i} />)}
          {!hasShortlist && !hasPlan && (
            <div className={`text-center py-12 text-[#9ca3af] ${textBase}`}>Generate a workflow shortlist to see recommendations.</div>
          )}
        </div>
      )}

      {/* 30/60/90 ROADMAP */}
      {activeSection === "roadmap" && hasPlan && (
        isProjectPaid ? (
          <Card className="border border-[#e5e7eb] bg-white">
            <CardContent className="p-6">
              <h3 className={`${headingSm} font-semibold text-[#111827] mb-6`}>30/60/90-Day Implementation Roadmap</h3>
              <RoadmapTimeline roadmap={plan.roadmap_30_60_90} />
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-[#e5e7eb] bg-white">
            <CardContent className="p-8 text-center">
              <Lock className="h-10 w-10 text-[#d1d5db] mx-auto mb-4" />
              <h3 className={`${headingSm} font-semibold text-[#111827] mb-2`}>Implementation Plan Locked</h3>
              <p className={`${textBase} text-[#9ca3af] max-w-md mx-auto`}>The 30/60/90-Day Roadmap will be available after the Implementation Project payment is recorded ({`$${pricing.implementation_project.toLocaleString()}`}).</p>
            </CardContent>
          </Card>
        )
      )}

      {/* PHASE 1 Scope of Services */}
      {activeSection === "sow" && hasPlan && (
        isProjectPaid ? (
          <Card className="border border-[#e5e7eb] bg-white">
            <CardContent className="p-6">
              <h3 className={`${headingSm} font-semibold text-[#111827] mb-6`}>Phase 1 — Scope of Services</h3>
              <SOWSection sow={plan.sow_starter} />
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-[#e5e7eb] bg-white">
            <CardContent className="p-8 text-center">
              <Lock className="h-10 w-10 text-[#d1d5db] mx-auto mb-4" />
              <h3 className={`${headingSm} font-semibold text-[#111827] mb-2`}>Scope of Services Locked</h3>
              <p className={`${textBase} text-[#9ca3af] max-w-md mx-auto`}>The Phase 1 Scope of Services will be available after the Implementation Project payment is recorded ({`$${pricing.implementation_project.toLocaleString()}`}).</p>
            </CardContent>
          </Card>
        )
      )}

      {/* AI DELIVERY STANDARD */}
      {activeSection === "standard" && hasPlan && (
        isProjectPaid ? (
          <Card className="border border-[#e5e7eb] bg-white">
            <CardContent className="p-6">
              <h3 className={`${headingSm} font-semibold text-[#111827] mb-4`}>Homogeneous AI Delivery Standard</h3>
              <DeliveryStandardSection standard={plan.delivery_standard} />
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-[#e5e7eb] bg-white">
            <CardContent className="p-8 text-center">
              <Lock className="h-10 w-10 text-[#d1d5db] mx-auto mb-4" />
              <h3 className={`${headingSm} font-semibold text-[#111827] mb-2`}>AI Delivery Standard Locked</h3>
              <p className={`${textBase} text-[#9ca3af] max-w-md mx-auto`}>The Delivery Standard will be available after the Implementation Project payment is recorded ({`$${pricing.implementation_project.toLocaleString()}`}).</p>
            </CardContent>
          </Card>
        )
      )}
    </div>
  );

  const handleExportPdf = async () => {
    if (!reportRef.current) return;
    setExportingPdf(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const filename = `${(prospectInfo?.company_name || "Assessment").replace(/\s+/g, "_")}_${isInitial ? "Initial" : "Detailed"}_AI_Readiness_Report.pdf`;
      await html2pdf()
        .set({
          margin: [12, 10, 12, 10],
          filename,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        })
        .from(reportRef.current)
        .save();
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Failed to export PDF", { description: e?.message });
    } finally {
      setExportingPdf(false);
    }
  };

  const handleEmailReport = async () => {
    if (!result?.prospect_id) {
      toast.error("No prospect linked — cannot send email");
      return;
    }
    if (!prospectInfo?.contact_email) {
      toast.error("Customer email not on file");
      return;
    }
    setEmailingReport(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const tenantId = session?.tenant?.id || "tenant_1";
      const userId = session?.user?.id || "admin";
      const role = session?.user?.role || "admin";
      const resp = await fetch(`${BACKEND_URL}/api/assessment/results/${result.id}/email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-tenant-id": tenantId,
          "x-user-id": userId,
          "x-user-role": role,
        },
        body: JSON.stringify({ prospect_id: result.prospect_id }),
      });
      if (!resp.ok) throw new Error((await resp.json())?.detail || "Failed to send");
      toast.success("Report emailed to client", { description: `Sent to ${prospectInfo.contact_email}` });
    } catch (e) {
      toast.error("Failed to email report", { description: e?.message });
    } finally {
      setEmailingReport(false);
    }
  };

  // ─── Main Render ─────
  return (
    <div data-testid="assessment-report" className={portalMode ? "w-full" : "max-w-6xl mx-auto"} ref={reportRef}>
      {thinkingType && (
        <ThinkingOverlay
          show={true}
          messages={REPORT_THINKING[thinkingType] || []}
          duration={5000}
        />
      )}
      {/* Header */}
      <div className="mb-6">
        {/* Top action bar: Back (left) + Print / Export PDF / Email (right) */}
        {!portalMode && (
          <div className="flex items-center justify-between mb-5 pb-4 border-b border-[#e5e7eb]">
            {onBack ? (
              <button
                onClick={() => {
                  if (result?.prospect_id && session?.user?.role !== "client") {
                    navigate(`/app/pipeline/${result.prospect_id}`);
                  } else {
                    onBack();
                  }
                }}
                className="text-[13px] font-medium text-[#374151] hover:text-[#111827] flex items-center gap-1.5 transition-colors"
                data-testid="back-from-report"
              >
                <ChevronLeft className="h-4 w-4" />
                {result?.prospect_id && session?.user?.role !== "client" ? "Back to Workflow" : "Back to Assessments"}
              </button>
            ) : <div />}
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-[#e5e7eb] bg-white text-[13px] font-semibold text-[#374151] hover:bg-[#f9fafb] transition-colors"
                data-testid="print-report-btn"
              >
                <Printer className="h-3.5 w-3.5" /> Print
              </button>
              <button
                onClick={handleExportPdf}
                disabled={exportingPdf}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-[#e5e7eb] bg-white text-[13px] font-semibold text-[#374151] hover:bg-[#f9fafb] transition-colors disabled:opacity-50"
                data-testid="export-pdf-btn"
              >
                {exportingPdf ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Export PDF
              </button>
              <button
                onClick={handleEmailReport}
                disabled={emailingReport}
                className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg text-[13px] font-semibold text-white shadow-[0_4px_12px_rgba(199,154,59,0.30)] hover:opacity-95 transition-opacity disabled:opacity-60"
                style={{ backgroundColor: "#C79A3B" }}
                data-testid="email-report-btn"
              >
                {emailingReport ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Email Report to Client
              </button>
            </div>
          </div>
        )}

        <div className="flex items-start justify-between gap-4 flex-wrap">
          {/* For admin DETAILED, CEOReportV2 renders its own hero (title + score + meta).
              Skip the duplicate header block here to avoid the "same title twice" bug. */}
          {portalMode || isInitial ? (
            <div>
              <h2 className={`${headingLg} font-semibold text-[#111827] tracking-tight`} data-testid="report-title">{reportTitle}</h2>
              {prospectInfo?.company_name && (
                <p className="text-[13px] text-[#6b7280] mt-1" data-testid="report-for">
                  {prospectInfo.company_name} · Generated {new Date(result.created_at || Date.now()).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </p>
              )}
              <div className="flex items-center gap-3 mt-3">
                <div className="flex items-baseline gap-1">
                  <div className="text-[32px] font-bold text-[#111827] tabular-nums leading-none">{result.overall_readiness_score}</div>
                  <div className={`${textBase} text-[#9ca3af]`}>/{result.scoring_mode === "growth" ? "5.0" : "4.0"}</div>
                </div>
                <Badge className={`${textSm} ${BAND_PILL[result.readiness_band] || ""}`}>
                  {result.readiness_band}
                </Badge>
                {gatingFlags.length > 0 && (
                  <Badge className="text-xs bg-[#fef2f2] text-[#ef4444] border-[#fecaca]">
                    <AlertTriangle className="h-3 w-3 mr-1" />{gatingFlags.length} Gating Flag{gatingFlags.length > 1 ? "s" : ""}
                  </Badge>
                )}
              </div>
            </div>
          ) : <div />}
          {/* Auto-generation handles workflows + plan server-side on assessment completion.
              These buttons remain available as a silent regenerate fallback for older results. */}
          {!isInitial && !hasShortlist && !hasPlan && (
            <div className="flex gap-2">
              <Button onClick={handleGenerateShortlist} disabled={generatingShortlist}
                className="h-9 bg-white text-[#374151] border border-[#e5e7eb] hover:bg-[#f9fafb] font-medium text-[12px]" data-testid="generate-shortlist-btn">
                {generatingShortlist ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Regenerating…</> : <><Sparkles className="h-3 w-3 mr-1.5" /> Regenerate Workflows</>}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Layout: portal mode renders ONLY content (Portal's left sidebar drives section).
          Admin: in-depth assessments → new contiguous CEO Report v2 (no tabs, no Q&A).
          Admin: preliminary assessments → keep existing tabbed layout. */}
      {portalMode ? (
        <div className="min-w-0">
          <ReportSectionContent />
        </div>
      ) : isInitial ? (
        <CEOReportV2
          result={result}
          prospectInfo={prospectInfo}
          tenantId={result?.provisioned_tenant_id || result?.tenant_id}
          initialMode={true}
          portalMode={portalMode}
          onUpgradeCTA={async () => {
            const tenId = result?.provisioned_tenant_id || result?.tenant_id;
            if (!tenId) return;
            if (portalMode) {
              // Customer view — bounce to the in-portal in-depth assessment CTA
              try {
                const BACKEND_URL3 = process.env.REACT_APP_BACKEND_URL;
                const r = await fetch(`${BACKEND_URL3}/api/tenants/${tenId}/purchase-assessment`, {
                  method: "POST", headers: { "Content-Type": "application/json" },
                });
                if (r.ok) {
                  window.history.replaceState({}, "", `/portal/${tenId}/payment/assessment-confirmed`);
                  window.dispatchEvent(new PopStateEvent("popstate"));
                }
              } catch {
                // no-op
              }
            } else {
              // Admin — jump into the in-depth assessment creation flow
              window.location.assign(`/app/assessment-templates`);
            }
          }}
        />
      ) : (
        <CEOReportV2
          result={result}
          prospectInfo={prospectInfo}
          tenantId={result?.provisioned_tenant_id}
          onConfirmScope={async (topThree) => {
            try {
              if (result?.prospect_id) {
                const BACKEND_URL2 = process.env.REACT_APP_BACKEND_URL;
                const tenantId2 = session?.tenant?.id || "tenant_1";
                const userId2 = session?.user?.id || "admin";
                const role2 = session?.user?.role || "admin";
                await fetch(`${BACKEND_URL2}/api/prospects/${result.prospect_id}`, {
                  method: "PATCH",
                  headers: {
                    "Content-Type": "application/json",
                    "x-tenant-id": tenantId2,
                    "x-user-id": userId2,
                    "x-user-role": role2,
                  },
                  body: JSON.stringify({
                    pre_selected_workflows: topThree,
                    report_confirmed_at: new Date().toISOString(),
                    report_confirmed_by: userId2,
                  }),
                });
                toast.success("Top 3 workflows pre-selected");
              }
              if (result?.provisioned_tenant_id) {
                navigate(`/portal/${result.provisioned_tenant_id}`);
              } else if (result?.prospect_id) {
                navigate(`/app/pipeline/${result.prospect_id}`);
              }
            } catch (e) {
              toast.error("Could not save scope confirmation", { description: e?.message });
            }
          }}
        />
      )}
    </div>
  );
};

export default AssessmentReport;
